import React, { useEffect, useRef, useState } from "react";
import "./DatePicker.scss";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import TextField from "@mui/material/TextField";
import moment from "moment";
import { useSelector } from "react-redux";
import { convertDateToUTC, convertYearToUTC} from "src/latest/src";
import { QuestionsUniqueKeys } from "src/containers/QuestionnaireParent/QuestionnaireEnum";

type Props = {
    title: string,
    placeholder: string,
    onChange: any,
    inputValue: string,
    currentQuestions?: any,
    validateQuestion?: any,
    onlyYear?: boolean,
    uniqueKey: boolean,
    question?: any,
    disabled?: boolean,
    feedbackClassName?: string,
};

function DatePicker({
    title,
    inputValue,
    placeholder = "MM-DD-YYYY",
    onChange,
    onlyYear = false,
    currentQuestions,
    validateQuestion,
    question,
    choice,
    disabled = false,
    feedbackClassName = "",
}: Props): React$Element<any> {
    const [value, setValue] = React.useState(new Date(inputValue));
    const [uniquekeyBaseFeedbackMessage, setUniquekeyBaseFeedbackMessage] = useState("");
    const [isValidDate, setIsValidDate] = React.useState();
    const [feedbackMessage, setFeedbackMessage] = React.useState("");
    const isDOB =
        question?.unique_key?.toLowerCase().includes("dateofbirth") || question?.unique_key?.toLowerCase().includes("dob") ||
        choice?.unique_key?.toLowerCase().includes("dateofbirth") || choice?.unique_key?.toLowerCase().includes("dob") || window.location.pathname.includes("clinician-availability");

    const ref = useRef(null);

    const isRequired = question?.required;
    const { isAuthed, loggedInUser } = useSelector((state) => state.authentication);
    const intakeType = useSelector((state) => state?.questionnaireData?.initialMetadata?.questionnaire_group?.type);
    const minimumAge =
        question?.unique_key === QuestionsUniqueKeys.patientsAgeSelfMatchingClientQuestionV1 ||
            question?.unique_key === QuestionsUniqueKeys.childdateOfBirthSelfMatchingClientQuestionV1 || window.location.pathname.includes("clinician-availability") || window.location.pathname.includes("/therapy/clinician-availability")
            ? 0
            : 18;
    const maxAge =
        question?.unique_key === QuestionsUniqueKeys.childdateOfBirthSelfMatchingClientQuestionV1 ? 18 : 999;

        useEffect(() => {
            if (inputValue) {
                let utcDate;
        
                if (inputValue.length === 4) {
                    utcDate = convertYearToUTC(inputValue);
                } else {
                    utcDate = convertDateToUTC(inputValue);
                }
        
                setValue(utcDate);
                setIsValidDate(true);
                setFeedbackMessage("");
            } else {
                setValue(""); // Clear input when value is null
            }
        }, [inputValue]);

    useEffect(()=>{
        (question?.unique_key=== QuestionsUniqueKeys.yearbeginpracticeTherapistQuestionV1 || question?.unique_key=== QuestionsUniqueKeys.yeargraduatedTherapistQuestionV1) ? setUniquekeyBaseFeedbackMessage("Enter Valid Year") :  setUniquekeyBaseFeedbackMessage("DOB should not be empty");
    },[]);

    const handleChange = (newValue) => {
        // Handle invalid input
        if (!newValue) {
            onChange("");
            setIsValidDate(false);
            setFeedbackMessage(uniquekeyBaseFeedbackMessage);

            return;
        } else if (isNaN(newValue.getTime())) {
            onChange("");
            setIsValidDate(false);
            setFeedbackMessage("Provided DOB is not valid");

            return;
        }

        if (
            (newValue && newValue?.getFullYear() < 1900) ||
            newValue?.getFullYear() > new Date().getFullYear()
        ) {
            /* if the selected year is earlier than 1900 or later than the current year, don't update the state */
            onChange("");
            setIsValidDate(false);
        } else {
            setValue(newValue);
            const userAge = moment().diff(
                moment(newValue, "MM/DD/YYYY"),
                "years"
            );

            if (!onlyYear && isDOB) {

                if (
                    userAge >= minimumAge && userAge < maxAge &&
                    moment(newValue, "MM/DD/YYYY", true).isValid()
                ) {
                    onChange(
                        moment(newValue).format("MM/DD/YYYY").toString() ||
                        ""
                    );
                    setFeedbackMessage("");
                    setIsValidDate(true);
                } else {
                    onChange("");

                    // do not show validation when empty
                    if (userAge < minimumAge) {
                        if (!isNaN(userAge)) {
                            setIsValidDate(false);
                            setFeedbackMessage(
                                "Must be " + minimumAge + " years old"
                            );
                        }
                    }

                    if (userAge >= maxAge) {
                        if (!isNaN(userAge)) {
                            setIsValidDate(false);
                            setFeedbackMessage(
                                "Your child's age must be below " +
                                maxAge +
                                " years"
                            );
                        }
                    }
                }
            } else {
                // Current Year Validation for certification
                if (userAge >= 0 && validateQuestion) {
                    return ValidateYear(
                        moment(newValue).format("YYYY").toString()
                    );
                } else if (userAge >= 0) {
                    onChange(
                        moment(newValue).format("YYYY").toString() || ""
                    );
                    setIsValidDate(true);
                } else {
                    onChange("");
                    setIsValidDate(false);
                }
            }
        }
    };

    const ValidateYear = (year) => {
        const value = currentQuestions.filter(
            (question) => question.unique_key === validateQuestion
        )[0].choices[0]?.value;

        if (value && Number(value) > Number(year)) {
            onChange("");
            setIsValidDate(false);
        } else {
            onChange(year || "");
            setIsValidDate(true);
        }
    };

    return (
        <div className="date-container">
            {title && title !== "" && (
                <span className="date-title">{title}</span>
            )}
            <div
                className={
                    isRequired
                        ? isValidDate === false
                            ? "is-invalid date-picker"
                            : isValidDate
                                ? " date-picker"
                                : "date-picker"
                        : "date-picker"
                }
            >
                {/* <div className='label'>Date of Birth</div> */}
                {!onlyYear && (
                    <>
                        {" "}
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            dateFormat="monthAndYear"
                        >
                            <DesktopDatePicker
                                disabled={
                                    isAuthed === true &&
                                    Object.entries(loggedInUser).length > 0 &&
                                    intakeType ===
                                    "SELF_MATCHING_CLIENT_INTAKE" &&
                                    choice?.unique_key ===
                                    QuestionsUniqueKeys.patientdateofbirthSelfMatchingClientChoiceV1 || disabled
                                }
                                inputFormat="MM/dd/yyyy"
                                value={value}
                                maxDate={new Date()}
                                onChange={handleChange}
                                ref={ref}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputProps={{
                                            ...params.inputProps,
                                            placeholder,
                                            autoComplete: "off",
                                        }}
                                    />
                                )}
                                invalidLabel={"invalid label"}
                            />
                        </LocalizationProvider>
                    </>
                )}
                {onlyYear && (
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        dateFormat="monthAndYear"
                    >
                        <DesktopDatePicker
                            inputFormat="yyyy"
                            disabled={disabled}
                            value={value}
                            onChange={handleChange}
                            views={["year"]}
                            maxDate={new Date()}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        placeholder,
                                        autoComplete: "off",
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                )}
            </div>
            {
                feedbackMessage?.length > 0 &&
                <div className={!isValidDate ? `invalid-feedback mt-1 ${feedbackClassName}` : ""}>
                    {feedbackMessage}
                </div>
            }
        </div>
    );
}

export default DatePicker;
